import type { Field } from '@sitecore-content-sdk/nextjs';
import { useSitecore } from '@sitecore-content-sdk/nextjs';

/** Route slice exposed on the compatibility context (layout route may include `fields`). */
export type SitecoreCompatRoute = {
  fields?: Record<string, unknown>;
  itemId?: string;
  templateId?: string;
  [key: string]: unknown;
};

/** Subset of site settings fields used by migrated JSS components. */
export type SiteSettingsCompat = {
  globalSearchSourceId?: Field<string>;
  globalSearchWidgetId?: Field<string>;
  globalRecommendationWidgetId?: Field<string>;
  noOfRelatedArticlesCount?: Field<number>;
  siteAlerts?: unknown[];
  socialShareLinks?: unknown[];
  hideLiveChat?: Field<boolean>;
  youTubeBaseURL?: Field<string>;
};

export type SitecoreCompatContext = Record<string, unknown> & {
  route?: SitecoreCompatRoute;
  site?: { name?: string; [key: string]: unknown };
  /** Often read from layout in JSS apps; mirror route item id when present. */
  itemId?: string;
  itemPath?: string;
  /** Populated by some layouts for inline SVG caching (JSS parity). */
  svgCache?: Record<string, string>;
  languages?: {
    isoCode: string;
    countryCode?: string;
    nativeName?: string;
    [key: string]: unknown;
  }[];
  siteSettings?: SiteSettingsCompat;
};

/**
 * JSS `useSitecoreContext()` compatibility: merges `layout.sitecore.context` with `route`
 * so existing components can keep using `sitecoreContext?.route`, `sitecoreContext?.siteSettings`, etc.
 */
export function useSitecoreContext(): {
  sitecoreContext?: SitecoreCompatContext;
} {
  const { page } = useSitecore();
  const sitecore = page?.layout?.sitecore;
  if (!sitecore) {
    return { sitecoreContext: undefined };
  }
  const ctx = (sitecore.context || {}) as Record<string, unknown>;
  const route = sitecore.route as unknown as SitecoreCompatRoute | undefined;
  const routeItemId = typeof route?.itemId === 'string' ? route.itemId : undefined;
  return {
    sitecoreContext: {
      ...ctx,
      route,
      itemId: (ctx.itemId as string | undefined) ?? routeItemId,
      siteSettings: ctx.siteSettings as SiteSettingsCompat | undefined,
    } as SitecoreCompatContext,
  };
}
